import { create } from "zustand";
import { defaultLayout, towerLevelBlock } from "@empire/shared";
import {
  defenceBudget,
  emptyLayout,
  idx,
  isCore,
  isEdge,
  maxTowersOfOneType,
  towerSlots,
  towerStats,
  validateLayout,
  type Coord,
  type Layout,
  type Tile,
  type TileKind,
  type TowerKind,
  type ValidationResult,
} from "@empire/core-sim";

/**
 * The defence editor's state (S07, stories S2-2 to S2-4).
 *
 * The important thing here is that the editor validates with the SAME function
 * the server uses to accept a layout. There is no second, friendlier copy of
 * the rules living in the client — so the editor can never let a defender build
 * something the server will later refuse. That holds for towers too: the
 * one-type cap, the Guild unlocks and the slot-sees-the-road rule are all
 * core-sim's, and this file only ever reports what it says.
 */

/** What the current tool paints. "select" places, inspects and moves towers. */
export type Brush =
  | "road"
  | "mud"
  | "barricade"
  | "tower_slot"
  | "gate"
  | "erase"
  | "select";

const UNDO_LIMIT = 20;

/** Long-press cycles a road tile through its surface types. */
const TILE_CYCLE: readonly TileKind[] = ["road", "mud", "barricade"];

interface Snapshot {
  readonly tiles: readonly Tile[];
  readonly gate: Coord;
}

/**
 * One press-drag-release. Held so that a long-press can undo whatever the
 * press already painted, and so that releasing on a different tile can be read
 * as "move this tower there".
 */
interface Stroke {
  readonly base: Snapshot;
  readonly start: Coord;
  last: Coord;
  /** Set by a long-press: the rest of the drag is ignored. */
  cancelled: boolean;
  /** The last tile a road-type brush actually laid in this stroke, to keep the road joined. */
  lastLaid?: Coord;
  /** Messages already shown during this stroke, so a drag does not repeat them. */
  warned?: readonly string[];
}

/** Why the last touch did nothing. Shown briefly over the board. */
export interface Notice {
  readonly text: string;
  readonly at: Coord | null;
  /** Bumps on every notice, so the same text twice still shows twice. */
  readonly seq: number;
}

export interface BudgetLine {
  readonly label: string;
  readonly used: number;
  readonly allowed: number;
}

interface EditorState {
  layout: Layout;
  brush: Brush;
  validation: ValidationResult;

  /** Armed tower type. Tapping a free slot builds it. */
  towerBrush: TowerKind | null;
  /** The slot the player is inspecting, if any. */
  selected: Coord | null;

  past: Snapshot[];
  future: Snapshot[];
  stroke: Stroke | null;
  /**
   * Where an armed tower will go when the finger lifts. Towers are placed on
   * RELEASE, not on press: the finger hides the tile, so the player sees the
   * outline, slides to the right square, and only then commits.
   */
  pending: Coord | null;
  notice: Notice | null;
  clearNotice: () => void;

  setBrush: (brush: Brush) => void;
  setTowerBrush: (kind: TowerKind | null) => void;
  select: (at: Coord | null) => void;
  /**
   * In the real game these come from the player's village. The editor exposes
   * them so a defender can see what a Wall or Guild upgrade would let them
   * build before they pay for it.
   */
  setLevels: (levels: { wallLevel?: number; engineersGuildLevel?: number }) => void;
  paint: (at: Coord) => void;
  /** Long-press on a road tile: road -> mud -> barricade -> road. */
  longPress: (at: Coord) => void;
  /** Called when a drag ends: one drag is one undo step, not one per tile. */
  commit: () => void;
  undo: () => void;
  redo: () => void;
  reset: () => void;
  /**
   * Put the default road back (29 Sep 2026), at this board's levels, as one
   * undoable step.
   */
  resetToDefault: () => void;
  /**
   * Empty the board so the defender can draw their own, as one undoable step.
   * Keeps the Wall and Guild levels (they come from the village) — `reset()`
   * does not, which is why the button does not call it. An empty board is not
   * a defence, so it is never saved: leaving the editor with it puts the
   * standing defence back (see DefenceEditor).
   */
  clearBoard: () => void;
  /** Replace the board with one that came from somewhere else (a saved layout). */
  load: (layout: Layout) => void;

  buildTower: (at: Coord, kind: TowerKind) => void;
  removeTower: (at: Coord) => void;
  /** Raise a built tower one level, if the Guild allows it (D-119). Paid on save. */
  upgradeTower: (at: Coord) => void;

  canUndo: () => boolean;
  canRedo: () => boolean;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, Math.trunc(value)));
}

function snapshot(layout: Layout): Snapshot {
  return { tiles: layout.tiles.map(copyTile), gate: layout.gate };
}

function copyTile(tile: Tile): Tile {
  return tile.tower ? { kind: tile.kind, tower: { ...tile.tower } } : { kind: tile.kind };
}

function restore(layout: Layout, snap: Snapshot): Layout {
  return { ...layout, tiles: snap.tiles.map(copyTile), gate: snap.gate };
}

function same(a: Coord | null, b: Coord | null): boolean {
  if (!a || !b) return a === b;
  return a.x === b.x && a.y === b.y;
}

let noticeSeq = 0;

/** Say why a touch did nothing — once per message per stroke. */
function warn(stroke: Stroke, text: string, at: Coord | null): void {
  if (stroke.warned?.includes(text)) return;
  const store = useEditorStore.getState();
  useEditorStore.setState({
    notice: { text, at, seq: ++noticeSeq },
    stroke: store.stroke ? { ...store.stroke, warned: [...(store.stroke.warned ?? []), text] } : store.stroke,
  });
}

function tileAt(layout: Layout, at: Coord): Tile | undefined {
  return layout.tiles[idx(at.x, at.y, layout.size)];
}

function isRoadKind(kind: TileKind | undefined): boolean {
  return kind === "road" || kind === "mud" || kind === "barricade";
}

/** Is any of the four tiles next to `at` road? */
function hasRoadNeighbour(layout: Layout, at: Coord): boolean {
  return [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ].some(([dx, dy]) => {
    const x = at.x + dx!;
    const y = at.y + dy!;
    if (x < 0 || y < 0 || x >= layout.size || y >= layout.size) return false;
    return isRoadKind(layout.tiles[idx(x, y, layout.size)]?.kind);
  });
}

/** The kind a brush lays down, or null for tools that do something else. */
function kindFor(brush: Brush): TileKind | null {
  switch (brush) {
    case "road":
      return "road";
    case "mud":
      return "mud";
    case "barricade":
      return "barricade";
    case "tower_slot":
      return "tower_slot";
    case "erase":
      return "empty";
    case "gate":
    case "select":
      return null;
  }
}


/**
 * Why `kind` cannot be laid at `at` right now, or null if it can.
 *
 * This is the question "may the finger do this?", asked BEFORE the tile
 * changes. Budgets are core-sim's numbers (the same ones validateLayout
 * enforces), so a stroke that runs out simply stops — the defender never
 * builds a board the validator then has to reject.
 */
export function placementBlock(layout: Layout, at: Coord, kind: TileKind): string | null {
  const tile = tileAt(layout, at);
  if (!tile || tile.kind === "core") return null;
  if (tile.kind === kind) return null;

  if (isRoadKind(kind)) {
    if (tile.kind === "tower_slot") {
      return tile.tower ? "A tower stands here. Erase it first." : "A tower spot is here. Erase it first.";
    }
    const budget = defenceBudget(layout.wallLevel);
    const lines = budgetLines(layout);
    const used = (label: string) => lines.find((l) => l.label === label)!.used;
    if (!isRoadKind(tile.kind) && used("road") >= budget.roadTiles) {
      return `No road left: Wall ${layout.wallLevel} allows ${budget.roadTiles} tiles. Upgrade the Wall for more.`;
    }
    if (kind === "mud" && used("mud") >= budget.mudTiles) {
      return budget.mudTiles === 0 ? "Mud unlocks at Wall 3." : `No mud left (${budget.mudTiles} of ${budget.mudTiles}).`;
    }
    if (kind === "barricade" && used("barricade") >= budget.barricades) {
      return `No barricades left (${budget.barricades} of ${budget.barricades}).`;
    }
    return null;
  }

  if (kind === "tower_slot") {
    if (isRoadKind(tile.kind)) return "Towers go beside the road, not on it.";
    if (tile.kind !== "empty") return "Something is already here.";
    const allowed = towerSlots(layout.engineersGuildLevel);
    const used = layout.tiles.filter((t) => t.kind === "tower_slot").length;
    if (used >= allowed) {
      return `All ${allowed} tower places are used. Upgrade the Engineers' Guild for more.`;
    }
    return null;
  }
  return null;
}

/** Why an armed tower cannot go on this tile, or null if it can. */
function towerBlock(layout: Layout, at: Coord, kind: TowerKind): string | null {
  const tile = tileAt(layout, at);
  if (!tile) return null;
  if (tile.kind === "tower_slot" && tile.tower) return null; // selects instead
  const reason = towerUnavailable(layout, kind);
  if (reason) return `${towerStats(kind, 1).name}: ${reason.toLowerCase()}.`;
  if (tile.kind === "tower_slot") return null;
  return placementBlock(layout, at, "tower_slot");
}

/**
 * The tiles to lay between the last laid tile and this one, so a road drawn
 * with a finger stays joined. Crossing a tile's corner on an isometric board
 * jumps diagonally in the grid, and a quick finger skips tiles; both used to
 * leave gaps (or, worse, lay the tile next door). Returns the path WITHOUT the
 * start, ending at `to`; empty when the jump is too long to guess at.
 */
export function joinPath(layout: Layout, from: Coord, to: Coord): Coord[] {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  if (Math.abs(dx) + Math.abs(dy) <= 1) return [to];
  if (Math.max(Math.abs(dx), Math.abs(dy)) > 3) return [to];
  const sx = Math.sign(dx);
  const sy = Math.sign(dy);
  const out: Coord[] = [];
  let x = from.x;
  let y = from.y;
  // Step one axis at a time, preferring a step onto a tile that is already
  // road, then one that can take road, so the joint follows the finger's side.
  const good = (c: Coord) => {
    const t = tileAt(layout, c);
    return t ? (isRoadKind(t.kind) ? 2 : t.kind === "empty" ? 1 : 0) : -1;
  };
  while (x !== to.x || y !== to.y) {
    const alongX = x !== to.x ? { x: x + sx, y } : null;
    const alongY = y !== to.y ? { x, y: y + sy } : null;
    let step: Coord;
    if (alongX && alongY) {
      const remX = Math.abs(to.x - x);
      const remY = Math.abs(to.y - y);
      step = remX > remY ? alongX : remY > remX ? alongY : good(alongY) > good(alongX) ? alongY : alongX;
    } else step = (alongX ?? alongY)!;
    x = step.x;
    y = step.y;
    out.push(step);
  }
  return out;
}

/**
 * The four numbers under the board.
 *
 * Counted exactly the way core-sim's `checkBudgets` counts them — mud and
 * barricades sit ON the road, so they are inside the road total as well as
 * having their own allowance. Showing them any other way would have the strip
 * disagree with the error message directly beneath it.
 */
export function budgetLines(layout: Layout): BudgetLine[] {
  const budget = defenceBudget(layout.wallLevel);

  let road = 0;
  let mud = 0;
  let barricade = 0;
  let slots = 0;

  for (const tile of layout.tiles) {
    if (isRoadKind(tile.kind)) road += 1;
    if (tile.kind === "mud") mud += 1;
    if (tile.kind === "barricade") barricade += 1;
    if (tile.kind === "tower_slot") slots += 1;
  }

  return [
    { label: "road", used: road, allowed: budget.roadTiles },
    { label: "mud", used: mud, allowed: budget.mudTiles },
    { label: "barricade", used: barricade, allowed: budget.barricades },
    { label: "towers", used: slots, allowed: towerSlots(layout.engineersGuildLevel) },
  ];
}

/** How many of this tower type are already built. */
export function builtOfKind(layout: Layout, kind: TowerKind): number {
  return layout.tiles.filter((t) => t.tower?.kind === kind).length;
}

/**
 * Why a tower type cannot be placed right now, or null if it can.
 *
 * The reasons mirror core-sim's checks; the point of duplicating the QUESTION
 * here is to grey the button out before the tap, not to duplicate the ANSWER —
 * validateLayout still has the last word after every placement.
 */
export function towerUnavailable(layout: Layout, kind: TowerKind): string | null {
  const stats = towerStats(kind, 1);
  if (layout.engineersGuildLevel < stats.unlockEngineersGuild) {
    return `Unlocks at Guild ${stats.unlockEngineersGuild}`;
  }
  const limit = maxTowersOfOneType(layout.engineersGuildLevel);
  if (builtOfKind(layout, kind) >= limit) {
    return `Limit ${limit} of one type`;
  }
  return null;
}

export const useEditorStore = create<EditorState>((set, get) => ({
  layout: emptyLayout(),
  brush: "road",
  validation: validateLayout(emptyLayout()),

  towerBrush: null,
  selected: null,

  past: [],
  future: [],
  stroke: null,
  pending: null,
  notice: null,
  clearNotice: () => set({ notice: null }),

  setBrush: (brush) =>
    set({ brush, towerBrush: brush === "select" ? get().towerBrush : null, selected: null }),

  setTowerBrush: (kind) => set({ brush: "select", towerBrush: kind, selected: null }),

  select: (at) => set({ selected: at }),

  setLevels: ({ wallLevel, engineersGuildLevel }) => {
    const { layout } = get();
    const next: Layout = {
      ...layout,
      wallLevel: clamp(wallLevel ?? layout.wallLevel, 0, 10),
      engineersGuildLevel: clamp(engineersGuildLevel ?? layout.engineersGuildLevel, 0, 20),
    };
    set({ layout: next, validation: validateLayout(next) });
  },

  paint: (at) => {
    const state = get();
    const { layout, brush } = state;

    // Remember where this stroke began, so a long-press can undo it and a
    // release on another tile can be read as a move.
    const stroke: Stroke = state.stroke
      ? { ...state.stroke, last: at }
      : { base: snapshot(layout), start: at, last: at, cancelled: false };
    set({ stroke });
    if (stroke.cancelled) return;

    // The core is not editable. Silently ignoring the stroke is better than an
    // error: the defender is dragging across the board, not making a mistake.
    if (isCore(at.x, at.y)) return;

    if (brush === "select") {
      const tile = tileAt(layout, at);
      const armed = state.towerBrush;
      // A built tower is inspected (and can be dragged to a free spot) —
      // but only when the press STARTS on it. Sliding an armed tower over one
      // must not jump the selection around under the finger.
      if (tile?.kind === "tower_slot" && tile.tower) {
        if (same(stroke.start, at)) set({ selected: at, pending: null });
        else if (armed) set({ pending: null });
        return;
      }
      const startTile = tileAt(layout, stroke.start);
      if (startTile?.kind === "tower_slot" && startTile.tower) {
        // Dragging a built tower: it moves on release (see commit). Outline
        // where it would land, if it can.
        const free = tile?.kind === "empty" || (tile?.kind === "tower_slot" && !tile.tower);
        if (!same(stroke.start, at) && !free && isRoadKind(tile?.kind)) {
          warn(stroke, "Towers go beside the road, not on it.", at);
        }
        set({ pending: free && !same(stroke.start, at) ? at : null });
        return;
      }
      if (armed) {
        // Towers go down when the finger lifts (see commit), so the player can
        // slide to the right square first. Here we only move the outline.
        const reason = towerBlock(layout, at, armed);
        set({ pending: reason ? null : at, selected: null });
        if (reason) warn(stroke, reason, at);
        return;
      }
      // Towers tool, nothing armed, a tap on open ground: this used to do
      // nothing at all, which on a phone reads as "the game is stuck" — most
      // often after tapping the tower card a second time, which disarms it.
      if (!armed && tile?.kind === "empty" && same(stroke.start, at)) {
        warn(stroke, "Pick a tower above first, then tap here.", at);
      }
      set({ selected: tile?.kind === "tower_slot" && same(stroke.start, at) ? at : null });
      return;
    }

    if (brush === "gate") {
      // A gate is only meaningful on an edge tile, and it must be road.
      if (!isEdge(at.x, at.y, layout.size)) {
        // Said, not silent: a tap inside the board used to do nothing.
        if (same(stroke.start, at)) warn(stroke, "The gate goes on the edge of the board.", at);
        return;
      }
      const here = tileAt(layout, at);
      if (here?.kind === "tower_slot") {
        warn(stroke, "A tower spot is here. Erase it first.", at);
        return;
      }
      if (!isRoadKind(here?.kind)) {
        const reason = placementBlock(layout, at, "road");
        if (reason) {
          warn(stroke, reason, at);
          return;
        }
      }
      const tiles = [...layout.tiles];
      // Moving the gate takes the old one with it when it was only a gate — a
      // lone road tile with no road beside it. Left behind, it was a stray
      // road piece the road step then refused ("Every road tile must connect
      // to the path from the gate") after a first tap on the wrong edge tile.
      const old = layout.gate;
      if (!same(old, at) && isRoadKind(tileAt(layout, old)?.kind) && !hasRoadNeighbour(layout, old)) {
        tiles[idx(old.x, old.y, layout.size)] = { kind: "empty" };
      }
      tiles[idx(at.x, at.y, layout.size)] = { kind: "road" };
      const next = { ...layout, tiles, gate: at };
      set({ layout: next, validation: validateLayout(next), selected: null });
      return;
    }

    const kind = kindFor(brush);
    if (!kind) return;

    // Road-type brushes lay a JOINED road: fill any tile the finger skipped
    // or clipped the corner of since the last one laid.
    const route = isRoadKind(kind) && stroke.lastLaid ? joinPath(layout, stroke.lastLaid, at) : [at];

    let next = layout;
    let lastLaid = stroke.lastLaid;
    for (const c of route) {
      if (isCore(c.x, c.y)) continue;
      const position = idx(c.x, c.y, next.size);
      const current = next.tiles[position];
      if (current?.kind === kind) {
        lastLaid = c;
        continue;
      }
      // Towers and their spots are never painted over by a road brush, and
      // nothing goes past the budget. Erase is the one tool that removes.
      const reason = kind === "empty" ? null : placementBlock(next, c, kind);
      if (reason) {
        warn(get().stroke ?? stroke, reason, c);
        break;
      }
      const tiles = [...next.tiles];
      tiles[position] = { kind };
      next = { ...next, tiles };
      lastLaid = c;
    }

    const live = get().stroke ?? stroke;
    set({ stroke: { ...live, lastLaid } });
    if (next === layout) return;
    set({
      layout: next,
      validation: validateLayout(next),
      // Painting over the inspected slot ends the inspection.
      selected: route.some((c) => same(get().selected, c)) ? null : get().selected,
    });
  },

  /**
   * Long-press converts a road tile in place.
   *
   * The press has already painted by the time the timer fires, so the first
   * thing this does is put the board back the way it was. That is why a stroke
   * keeps a snapshot of where it started.
   */
  longPress: (at) => {
    const state = get();
    const stroke = state.stroke;
    if (!stroke || stroke.cancelled) return;
    // Only the road tools convert. Holding a finger on the road with Towers or
    // Erase selected turned it into a barricade in the friends test — nobody
    // holding the Towers tool means "make this a barricade".
    if (!isRoadKind(kindFor(state.brush) ?? undefined)) return;
    // A hold only counts if the finger never left the tile it started on. The
    // view cancels its timer on a move, but this is the rule itself, and a
    // dropped timer must not be able to convert a tile halfway through a drag.
    if (!same(stroke.start, at) || !same(stroke.start, stroke.last)) return;

    const reverted = restore(state.layout, stroke.base);
    const current = tileAt(reverted, at)?.kind;

    // Nothing to convert here, so this is not a long-press at all — leave the
    // stroke running. Cancelling it instead would kill the drag that is still
    // in progress: hold on a tower slot for half a second, then drag it to
    // another slot, and the move would silently do nothing.
    if (!isRoadKind(current)) return;

    // The next surface the budget allows. Road always is (the tile is road
    // already), so a Wall-0 hold just says why mud is not there yet.
    const from = TILE_CYCLE.indexOf(current!);
    let next: TileKind = "road";
    let refused: string | null = null;
    for (let step = 1; step <= TILE_CYCLE.length; step++) {
      const candidate = TILE_CYCLE[(from + step) % TILE_CYCLE.length]!;
      const reason = candidate === "road" ? null : placementBlock(reverted, at, candidate);
      if (!reason) {
        next = candidate;
        break;
      }
      refused ??= reason;
    }
    if (next === current) {
      // Nothing to convert into. Say why, but leave the stroke RUNNING: a
      // finger that rested on a tile before dragging is starting a road, and
      // cancelling here silently killed the rest of that drag.
      if (refused) warn(stroke, refused, at);
      return;
    }
    const tiles = [...reverted.tiles];
    tiles[idx(at.x, at.y, reverted.size)] = { kind: next };
    const layout = { ...reverted, tiles };

    set({
      layout,
      validation: validateLayout(layout),
      stroke: { ...stroke, cancelled: true },
    });
  },

  /**
   * History is captured at the END of a stroke. Painting a fifteen-tile road
   * should be one Ctrl+Z, not fifteen.
   */
  commit: () => {
    const state = get();
    const stroke = state.stroke;

    // A drag in select mode from a built tower onto a free slot moves it.
    if (
      stroke &&
      !stroke.cancelled &&
      state.brush === "select" &&
      !same(stroke.start, stroke.last)
    ) {
      const from = tileAt(state.layout, stroke.start);
      const to = tileAt(state.layout, stroke.last);
      const toFree = to?.kind === "empty" || (to?.kind === "tower_slot" && !to.tower);
      if (from?.kind === "tower_slot" && from.tower && toFree && !isCore(stroke.last.x, stroke.last.y)) {
        const tiles = [...state.layout.tiles];
        tiles[idx(stroke.last.x, stroke.last.y, state.layout.size)] = {
          kind: "tower_slot",
          tower: { ...from.tower },
        };
        // Onto open ground the tower takes its spot with it; onto a free spot
        // it leaves its old one behind. Either way the count of spots holds.
        tiles[idx(stroke.start.x, stroke.start.y, state.layout.size)] = {
          kind: to?.kind === "empty" ? "empty" : "tower_slot",
        };
        const layout = { ...state.layout, tiles };
        set({ layout, validation: validateLayout(layout), selected: null });
      }
    }

    // An armed tower goes where the finger lifted.
    const pending = get().pending;
    const armed = get().towerBrush;
    const movingTower = stroke ? tileAt(get().layout, stroke.start)?.tower : undefined;
    if (pending && armed && stroke && !stroke.cancelled && !movingTower && get().brush === "select") {
      set({ pending: null, stroke: null });
      const layout = get().layout;
      const tile = tileAt(layout, pending);
      if (tile?.kind === "empty") {
        const tiles = [...layout.tiles];
        tiles[idx(pending.x, pending.y, layout.size)] = { kind: "tower_slot" };
        const next = { ...layout, tiles };
        set({ layout: next, validation: validateLayout(next) });
      }
      get().buildTower(pending, armed);
      return;
    }

    set({ stroke: null, pending: null });

    const { layout, past } = get();
    const previous = past[past.length - 1];
    const current = snapshot(layout);

    // Nothing actually changed during the stroke.
    if (previous && JSON.stringify(previous) === JSON.stringify(current)) return;

    set({
      past: [...past, current].slice(-UNDO_LIMIT - 1),
      future: [],
    });
  },

  buildTower: (at, kind) => {
    const { layout } = get();
    const position = idx(at.x, at.y, layout.size);
    if (layout.tiles[position]?.kind !== "tower_slot") return;

    const tiles = [...layout.tiles];
    tiles[position] = { kind: "tower_slot", tower: { kind, level: 1 } };
    const next = { ...layout, tiles };
    // Not selected: selecting opened the tower card over the board after
    // every placement, in the middle of building (playtest, 29 Sep 2026).
    // Tap the tower to see it.
    set({ layout: next, validation: validateLayout(next), selected: null });
    get().commit();
  },

  removeTower: (at) => {
    const { layout } = get();
    const position = idx(at.x, at.y, layout.size);
    const tile = layout.tiles[position];
    if (tile?.kind !== "tower_slot" || !tile.tower) return;

    const tiles = [...layout.tiles];
    tiles[position] = { kind: "tower_slot" };
    const next = { ...layout, tiles };
    set({ layout: next, validation: validateLayout(next) });
    get().commit();
  },

  upgradeTower: (at) => {
    const { layout } = get();
    const position = idx(at.x, at.y, layout.size);
    const tile = layout.tiles[position];
    if (tile?.kind !== "tower_slot" || !tile.tower) return;
    const level = tile.tower.level + 1;
    const reason = towerLevelBlock(level, layout.engineersGuildLevel);
    if (reason) {
      useEditorStore.setState({ notice: { text: reason, at, seq: ++noticeSeq } });
      return;
    }
    const tiles = [...layout.tiles];
    tiles[position] = { kind: "tower_slot", tower: { ...tile.tower, level } };
    const next = { ...layout, tiles };
    set({ layout: next, validation: validateLayout(next) });
    get().commit();
  },

  undo: () => {
    const { past, future, layout } = get();
    if (past.length < 2) return;

    const target = past[past.length - 2]!;
    const next = restore(layout, target);
    set({
      layout: next,
      validation: validateLayout(next),
      past: past.slice(0, -1),
      future: [snapshot(layout), ...future].slice(0, UNDO_LIMIT),
      selected: null,
    });
  },

  redo: () => {
    const { past, future, layout } = get();
    const target = future[0];
    if (!target) return;

    const next = restore(layout, target);
    set({
      layout: next,
      validation: validateLayout(next),
      past: [...past, target].slice(-UNDO_LIMIT - 1),
      future: future.slice(1),
      selected: null,
    });
  },

  clearBoard: () => {
    const { layout } = get();
    const fresh = emptyLayout();
    // The gate stays where it was, as a road tile, so the very next thing
    // the defender does — drag from it — works.
    const tiles = fresh.tiles.map((t) => ({ ...t }));
    tiles[idx(layout.gate.x, layout.gate.y, fresh.size)] = { kind: "road" };
    const next: Layout = {
      ...fresh,
      tiles,
      gate: layout.gate,
      wallLevel: layout.wallLevel,
      engineersGuildLevel: layout.engineersGuildLevel,
    };
    set({ layout: next, validation: validateLayout(next), selected: null, pending: null, stroke: null });
    get().commit();
  },

  resetToDefault: () => {
    const { layout } = get();
    const next = defaultLayout({
      wallLevel: layout.wallLevel,
      engineersGuildLevel: layout.engineersGuildLevel,
    });
    set({ layout: next, validation: validateLayout(next), selected: null, pending: null, stroke: null });
    get().commit();
  },

  reset: () => {
    const fresh = emptyLayout();
    set({
      layout: fresh,
      validation: validateLayout(fresh),
      past: [snapshot(fresh)],
      future: [],
      stroke: null,
      selected: null,
      towerBrush: null,
      pending: null,
      notice: null,
    });
  },

  /**
   * History starts again from the loaded board.
   *
   * Keeping the old history would let one Ctrl+Z walk backwards out of the maze
   * the server just sent into whatever happened to be on screen before it —
   * which, on a fresh page load, is an empty board the defender never drew.
   */
  load: (layout) =>
    set({
      layout,
      validation: validateLayout(layout),
      past: [snapshot(layout)],
      future: [],
      stroke: null,
      selected: null,
      towerBrush: null,
      pending: null,
      notice: null,
    }),

  canUndo: () => get().past.length >= 2,
  canRedo: () => get().future.length > 0,
}));

// Seed the history with the starting board so the first undo has somewhere to
// go back to.
useEditorStore.getState().reset();

// Dev only: lets a test driver read the editor from the page.
if (import.meta.env.DEV) (globalThis as { __editor?: typeof useEditorStore }).__editor = useEditorStore;
